import { TableCell, TableRow } from '@mui/material'
import { Daycare, DaycareType, ReleaseActivation } from '@/types/Daycare'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker.tsx'
import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { SelectOptionsDeprecated } from '@/types/Select'
import styles from './otherReservationSettings.module.scss'

export interface ReleaseParametersFormValues {
    releaseActivation: string
    releaseWeekCountBefore: number
    releaseDayCountBefore: number
    releaseDay: string
    releaseHour: string
}

interface ReleaseActivationSelectProps {
    releaseParameters: ReleaseParametersFormValues
    onChange: (value: ReleaseParametersFormValues) => void
    daycareType: Daycare['type']
    canUpdate: boolean
}

const ReleaseActivationSelect: FunctionComponent<ReleaseActivationSelectProps> = ({
    releaseParameters,
    onChange,
    daycareType,
    canUpdate
}) => {
    const { t } = useTranslation()
    const { releaseActivation, releaseWeekCountBefore, releaseDayCountBefore, releaseDay, releaseHour } =
        releaseParameters

    const releaseOptions: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: t('expiration0'),
                    value: 'release_deactive'
                },
                {
                    name: t('dayActivated'),
                    value: 'release_day'
                },
                {
                    name: t('weekActivated'),
                    value: 'release_week'
                }
            ]
        }
    ]

    const selectDays: SelectOptionsDeprecated = [
        {
            options: [
                { value: '0', name: t('monday') },
                { value: '1', name: t('tuesday') },
                { value: '2', name: t('wednesday') },
                { value: '3', name: t('thursday') },
                { value: '4', name: t('friday') },
                { value: '5', name: t('saturday') },
                { value: '6', name: t('sunday') }
            ]
        }
    ]

    const isRPEorLAEP: boolean = daycareType === DaycareType.rpe || daycareType === DaycareType.laep

    return (
        <>
            <TableRow>
                <TableCell className={styles.RowText}>{t('releaseAuto')}</TableCell>
                <TableCell>
                    <SelectUnique
                        selectValue={releaseActivation}
                        setSelectValue={(value) => onChange({ ...releaseParameters, releaseActivation: value })}
                        selectOptions={releaseOptions}
                        readOnly={!canUpdate}
                        className={styles.UserInput}
                        formClassName={styles.UserInput}
                    />
                </TableCell>
            </TableRow>
            {releaseActivation !== ReleaseActivation.releaseDeactive && (
                <>
                    {releaseActivation === ReleaseActivation.releaseWeek && (
                        <>
                            <TableRow>
                                <TableCell className={styles.RowText}>
                                    {isRPEorLAEP ? t('numberOfWeeksBeforeAnimation') : t('numberOfWeeksBeforeSlot')}
                                </TableCell>
                                <TableCell>
                                    <UserInput
                                        type='number'
                                        value={releaseWeekCountBefore.toString()}
                                        disabled={!canUpdate}
                                        setValue={(value: string) => {
                                            onChange({ ...releaseParameters, releaseWeekCountBefore: parseInt(value) })
                                        }}
                                        endAdornment={`${releaseWeekCountBefore > 1 ? t('weeks') : t('week')}`}
                                        className={styles.UserInput}
                                        verify={'number'}
                                        minNumber={0}
                                        maxNumber={8}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className={styles.RowText}>{t('releaseDayOfWeek')}</TableCell>
                                <TableCell>
                                    <SelectUnique
                                        selectOptions={selectDays}
                                        label={t('day')}
                                        selectValue={releaseDay}
                                        setSelectValue={(value) =>
                                            onChange({ ...releaseParameters, releaseDay: value })
                                        }
                                        className={styles.UserInput}
                                        formClassName={styles.UserInput}
                                        readOnly={!canUpdate}
                                    />
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                    {releaseActivation === ReleaseActivation.releaseDay && (
                        <TableRow>
                            <TableCell className={styles.RowText}>
                                {isRPEorLAEP ? t('numberOfDaysBeforeAnimation') : t('numberOfDaysBeforeSlot')}
                            </TableCell>
                            <TableCell>
                                <UserInput
                                    type='number'
                                    value={releaseDayCountBefore.toString()}
                                    disabled={!canUpdate}
                                    setValue={(value: string) => {
                                        onChange({ ...releaseParameters, releaseDayCountBefore: parseInt(value) })
                                    }}
                                    endAdornment={`${releaseDayCountBefore > 1 ? t('days') : t('day')}`}
                                    className={styles.UserInput}
                                    verify={'number'}
                                    minNumber={1}
                                    maxNumber={30}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell className={styles.RowText}>{t('releaseHour')}</TableCell>
                        <TableCell>
                            <TimePickerComponent
                                value={releaseHour}
                                setValue={(value) => onChange({ ...releaseParameters, releaseHour: value })}
                                label={t('hour')}
                                minuteToDisable={30}
                                disabled={!canUpdate}
                                className={styles.SpecialInput}
                            />
                        </TableCell>
                    </TableRow>
                </>
            )}
        </>
    )
}

export default ReleaseActivationSelect
